import { useEffect, useRef } from 'react';

import mapboxgl from 'mapbox-gl';

const USER_DRAG_DISABLE_DURATION_MS = 10000; // 10 seconds

/**
 * Hook to track user drag and zoom events and disable programmatic flyTo for a period after user interaction
 * @param map - Mapbox map reference
 * @param mapLoaded - Whether the map is loaded
 * @returns Ref indicating whether flyTo is enabled (true = enabled, false = disabled)
 */
export function useUserDragTracking(
  map: React.RefObject<mapboxgl.Map | null>,
  mapLoaded: boolean
): React.MutableRefObject<boolean> {
  const flyToEnabledRef = useRef<boolean>(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUserZoomRef = useRef<boolean>(false);
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const disableFlyTo = () => {
    flyToEnabledRef.current = false;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Re-enable flyTo after 10 seconds
    timeoutRef.current = setTimeout(() => {
      flyToEnabledRef.current = true;
      timeoutRef.current = null;
    }, USER_DRAG_DISABLE_DURATION_MS);
  };

  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const mapInstance = map.current;
    const mapContainer = mapInstance.getContainer();

    // ===== DRAG EVENT HANDLERS =====
    const handleDragStart = () => {
      // dragstart is only fired for user-initiated drags, not programmatic moves
      disableFlyTo();
    };

    const handleDrag = () => {
      // Keep flyTo disabled while user is dragging
      flyToEnabledRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    const handleDragEnd = () => {
      disableFlyTo();
    };

    // ===== ZOOM EVENT HANDLERS =====
    const handleWheel = () => {
      // Wheel events are always user-initiated (mouse wheel zoom)
      isUserZoomRef.current = true;
      disableFlyTo();

      // Reset the flag after a short delay (wheel events happen before zoomstart)
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
      }
      wheelTimeoutRef.current = setTimeout(() => {
        isUserZoomRef.current = false;
        wheelTimeoutRef.current = null;
      }, 100);
    };

    const handleZoomStart = () => {
      // zoomstart fires for both user and programmatic zooms
      // If isUserZoomRef is true, it means a wheel/touch event preceded this
      if (isUserZoomRef.current) {
        disableFlyTo();
      }
    };

    const handleZoomEnd = () => {
      // Reset the flag after zoom ends
      isUserZoomRef.current = false;
    };

    // ===== ZOOM BUTTON CLICK HANDLERS =====
    const handleZoomButtonClick = (event: MouseEvent) => {
      // Check if the click is on a zoom control button within this map's container
      const target = event.target as HTMLElement;
      const isZoomButton =
        (target.closest('.mapboxgl-ctrl-zoom-in') !== null ||
          target.closest('.mapboxgl-ctrl-zoom-out') !== null ||
          target.closest('.custom-zoom-in') !== null ||
          target.closest('.custom-zoom-out') !== null) &&
        mapContainer.contains(target);

      if (isZoomButton) {
        isUserZoomRef.current = true;
        disableFlyTo();
        // Reset flag after zoom completes
        setTimeout(() => {
          isUserZoomRef.current = false;
        }, 500);
      }
    };

    // ===== TOUCH EVENT HANDLERS (for pinch zoom) =====
    let touchStartDistance: number | null = null;
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        // Two-finger touch (pinch gesture)
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        touchStartDistance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 2 && touchStartDistance !== null) {
        // Pinch gesture detected
        isUserZoomRef.current = true;
        disableFlyTo();
      }
    };

    const handleTouchEnd = () => {
      touchStartDistance = null;
    };

    // ===== EVENT LISTENERS =====
    // Mapbox map events
    mapInstance.on('dragstart', handleDragStart);
    mapInstance.on('drag', handleDrag);
    mapInstance.on('dragend', handleDragEnd);
    mapInstance.on('zoomstart', handleZoomStart);
    mapInstance.on('zoomend', handleZoomEnd);

    // DOM events on map container
    mapContainer.addEventListener('wheel', handleWheel, { passive: true });
    mapContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    mapContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
    mapContainer.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Listen for zoom button clicks (on document to catch dynamically added controls)
    document.addEventListener('click', handleZoomButtonClick, true);

    return () => {
      // Clean up mapbox events
      mapInstance.off('dragstart', handleDragStart);
      mapInstance.off('drag', handleDrag);
      mapInstance.off('dragend', handleDragEnd);
      mapInstance.off('zoomstart', handleZoomStart);
      mapInstance.off('zoomend', handleZoomEnd);

      // Clean up DOM events
      mapContainer.removeEventListener('wheel', handleWheel);
      mapContainer.removeEventListener('touchstart', handleTouchStart);
      mapContainer.removeEventListener('touchmove', handleTouchMove);
      mapContainer.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('click', handleZoomButtonClick, true);

      // Clear timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current);
        wheelTimeoutRef.current = null;
      }
    };
  }, [map, mapLoaded]);

  return flyToEnabledRef;
}
